// StatusPill — unified pill component for sentiment, lifecycle, PO status,
// and vendor coverage. One component, multiple variants. Each variant has
// its own enum of valid values; TypeScript discriminates so consumers can't
// pass a sentiment value to a PO-status pill.
//
// Usage:
//   <StatusPill variant="sentiment" value="Negative" />
//   <StatusPill variant="lifecycle" value="Pending" />
//   <StatusPill variant="po" value="Shipped" />
//   <StatusPill variant="coverage" value="Regional" />
//
// Visual: light fill, dark text, ~11px font, fully rounded. Matches .pill
// from tcs.css.

import {
  SENTIMENT_STYLES,
  LIFECYCLE_STYLES,
  PO_STATUS_STYLES,
  VENDOR_COVERAGE_STYLES,
  type FacilityLifecycle,
  type POStatus,
  type VendorCoverage,
} from '../design-tokens';
import type { FeedbackSentiment } from '../types';

type StatusPillProps =
  | { variant: 'sentiment'; value: FeedbackSentiment; className?: string }
  | { variant: 'lifecycle'; value: FacilityLifecycle; className?: string }
  | { variant: 'po'; value: POStatus; className?: string }
  | { variant: 'coverage'; value: VendorCoverage; className?: string };

// Neutral fallback when the style map doesn't recognize a value (e.g., DB drift
// adds a new status before the frontend ships a corresponding style). Without
// this, an unknown value used to throw 'cannot read properties of undefined
// (reading "fill")' and nuke the entire React tree — see incident 2026-05-14
// where 2 facilities with status='Onboarding' brought down My Accounts.
const UNKNOWN_STYLE: { fill: string; text: string; label: string } = {
  fill: '#F1EFE8',
  text: '#5F5E5A',
  label: '',
};

export function StatusPill(props: StatusPillProps) {
  const { variant, value, className } = props;
  let style: { fill: string; text: string; label: string } | undefined;
  switch (variant) {
    case 'sentiment':
      style = SENTIMENT_STYLES[value];
      break;
    case 'lifecycle':
      style = LIFECYCLE_STYLES[value];
      break;
    case 'po':
      style = PO_STATUS_STYLES[value];
      break;
    case 'coverage':
      style = VENDOR_COVERAGE_STYLES[value];
      break;
  }
  // Defensive: if a value isn't in its style map, surface the raw value in a
  // neutral pill rather than crashing. Logs once per render so drift is visible
  // without spamming.
  if (!style) {
    if (typeof console !== 'undefined') {
      console.warn(`[StatusPill] No style for variant="${variant}" value="${String(value)}". Falling back to neutral pill.`);
    }
    style = { ...UNKNOWN_STYLE, label: String(value ?? '—') };
  }
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-semibold leading-relaxed ${className ?? ''}`}
      style={{ backgroundColor: style.fill, color: style.text }}
    >
      {style.label}
    </span>
  );
}
